import { useEffect, useRef, useMemo } from "react";
import SavePoint from "@/utils/savePoint";
import { trackDressDesignEvent, trackCreateAbandon } from "@/utils/analytics";
import { logger } from "@/utils/logger";
import { getCreditCheckoutRestoreId } from "@/utils/creditCheckoutReturnUrl";
import {
  useCreatePageStore,
  type CreatePageState,
  type CreatePageAction,
} from "@/stores/createPageStore";
import { FormPersistenceService } from "@/services/formPersistenceService";
import {
  hasMeaningfulFormData,
  serializeFormState,
} from "../utils/formStateHelpers";
import { createAuthoritativeCreateRestoreTargetKey } from "../utils/urlParamHelpers";

interface UsePersistenceProps {
  state: CreatePageState;
  dispatch: React.Dispatch<CreatePageAction>;
  restorationTimestampRef: React.MutableRefObject<number | null>;
}

const STORAGE_KEY = "createPageVariations";
const EXPIRATION_HOURS = 1; // Match signed URL expiration (60 minutes)
const EXPIRATION_MS = EXPIRATION_HOURS * 60 * 60 * 1000;

function hasAuthoritativeCreateRestore(
  restorationSettled: boolean,
  includeSettledTargets = false
): boolean {
  if (typeof window === "undefined") return false;
  if (restorationSettled && !includeSettledTargets) return false;

  try {
    const currentUrl = new URL(window.location.href);
    const hasFreshStartIntent = currentUrl.searchParams.get("new") === "1";
    const hasScopedSavePointRestore =
      currentUrl.searchParams.get("restore_form") === "true" &&
      Boolean(
        currentUrl.searchParams.get("auth_handoff") ||
        currentUrl.searchParams.get("quote_resume_id") ||
        currentUrl.searchParams.get("credit_resume_id") ||
        getCreditCheckoutRestoreId(currentUrl)
      );
    return Boolean(
      createAuthoritativeCreateRestoreTargetKey({
        modifyParam: currentUrl.searchParams.get("modify"),
        ticketIdParam: currentUrl.searchParams.get("ticket_id"),
        galleryDesignParam: currentUrl.searchParams.get("gallery_design"),
      }) ||
      hasFreshStartIntent ||
      hasScopedSavePointRestore ||
      currentUrl.searchParams.get("_anon_work_adopted") === "0"
    );
  } catch {
    return false;
  }
}

/**
 * Hook to handle all persistence-related side effects:
 * - Auto-saves form state
 * - Persists variations to localStorage
 * - Warns before page unload
 */
export function usePersistence({
  state,
  dispatch,
  restorationTimestampRef,
}: UsePersistenceProps) {
  // Track page load time for time-on-page calculation
  const pageLoadTimeRef = useRef<number>(Date.now());
  // Exit handlers are intentionally installed once. Keep their input current
  // across the two-second debounce window and across React's effect timing so
  // an immediate client-side navigation can flush the exact latest edit.
  const latestStateRef = useRef(state);
  latestStateRef.current = state;
  const latestRestorationTimestampRef = useRef(restorationTimestampRef);
  latestRestorationTimestampRef.current = restorationTimestampRef;

  // Create stable string representations for dependency tracking
  // This ensures React detects changes to arrays reliably
  // Use agent state as single source of truth
  const chatHistoryKey = useMemo(
    () => JSON.stringify(state.agent.conversation.messages || []),
    [state.agent.conversation.messages]
  );
  const quickActionsKey = useMemo(
    () => JSON.stringify(state.agent.presets || []),
    [state.agent.presets]
  );

  // Auto-save: Save form state when it changes
  useEffect(() => {
    const currentState = latestStateRef.current;

    // An explicit URL target owns the store until restoration settles. Saving
    // the previous dress during that window can resurrect it on the next load.
    if (
      hasAuthoritativeCreateRestore(
        latestRestorationTimestampRef.current.current !== null
      )
    )
      return;

    // Save if there's any meaningful data
    if (!hasMeaningfulFormData(currentState)) {
      return;
    }

    // Don't auto-save if credit checkout is open (to preserve credit purchase save point)
    if (currentState.ui.modals.credit.checkout) return;

    const timeoutId = setTimeout(() => {
      const latestState = latestStateRef.current;

      // Don't auto-save if a restoration just happened (within 5 seconds)
      // This prevents auto-save from overwriting a recently restored state
      if (
        latestRestorationTimestampRef.current.current &&
        Date.now() - latestRestorationTimestampRef.current.current < 5000
      ) {
        logger.debug("Not auto-saving immediately after restoration", {
          component: "usePersistence",
          timeSinceRestoration:
            Date.now() - (latestRestorationTimestampRef.current.current || 0),
        });
        return;
      }

      // The URL can become authoritative during the debounce window.
      if (
        hasAuthoritativeCreateRestore(
          latestRestorationTimestampRef.current.current !== null
        )
      )
        return;

      if (
        !hasMeaningfulFormData(latestState) ||
        latestState.ui.modals.credit.checkout
      ) {
        return;
      }

      // Check if there's already a recent credit purchase save point - don't overwrite it
      const existingSavePoint = SavePoint.getLatestSavePoint();
      if (
        existingSavePoint &&
        existingSavePoint.source === "credit_purchase_redirect" &&
        Date.now() - existingSavePoint.savedAt < 30000
      ) {
        // Within 30 seconds
        logger.debug("Not auto-saving over recent credit purchase save point", {
          component: "usePersistence",
        });
        return;
      }

      FormPersistenceService.saveState({
        data: serializeFormState(latestState),
        source: "auto_save",
      });
    }, 2000); // Debounce 2 seconds

    return () => clearTimeout(timeoutId);
  }, [
    // Include all fields that are saved to ensure changes trigger auto-save
    state.form.data.prompt,
    state.form.data.color,
    state.form.data.backPrompt,
    state.form.data.length,
    state.form.data.sleeve,
    state.form.data.sleeveAddons,
    state.form.data.neckline,
    state.form.data.waistline,
    state.form.data.silhouette,
    state.form.data.fabric,
    state.form.data.uploadedFrontImages,
    state.form.data.uploadedBackImages,
    state.form.data.uploadedFrontImageUrls,
    state.form.data.uploadedBackImageUrls,
    state.form.data.isUploadPreset,
    state.form.data.isFrontUploadPreset,
    state.form.data.isBackUploadPreset,
    state.form.data.isFrontCustomPreset,
    state.form.data.isBackCustomPreset,
    state.form.currentStep,
    state.agent.designBrief,
    state.generation.images.front.url,
    state.generation.images.front.path,
    state.generation.images.front.designSpec,
    state.generation.images.back.url,
    state.generation.images.back.path,
    state.generation.images.back.designSpec,
    state.generation.images.front.original.url,
    state.generation.images.front.original.path,
    state.generation.images.front.original.designSpec,
    state.generation.images.back.original.url,
    state.generation.images.back.original.path,
    state.generation.images.back.original.designSpec,
    state.ticket.id,
    state.ui.modification.prompt,
    state.ui.modification.showInput,
    state.ui.view,
    state.ui.modals.credit.checkout,
    state.generation.images.variations.front,
    state.generation.images.variations.back,
    state.generation.images.selected.frontIndex,
    state.generation.images.selected.backIndex,
    // Use stable string representations to reliably detect changes
    chatHistoryKey,
    quickActionsKey,
    state.agent.pendingSubmittedMessage,
    state.agent.pendingSubmittedMessageId,
    state.agent.pendingComposerSeed,
    state.agent.pendingComposerRecovery,
    restorationTimestampRef,
  ]);

  // Variations Persistence: Save variations to localStorage when they change
  useEffect(() => {
    // Do not snapshot the pre-restoration store while an explicit URL/savepoint
    // target is replacing it. That stale write can survive the current render
    // and become authoritative on a later reload.
    if (
      hasAuthoritativeCreateRestore(
        latestRestorationTimestampRef.current.current !== null
      )
    ) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
      return;
    }

    const frontVariations =
      state.generation.images.variations.front.length > 0
        ? state.generation.images.variations.front
        : null;
    const backVariations =
      state.generation.images.variations.back.length > 0
        ? state.generation.images.variations.back
        : null;

    // Only save if we have variations
    if (frontVariations || backVariations) {
      const dataToSave = {
        frontVariations,
        backVariations,
        selectedFrontVariationIndex:
          state.generation.images.selected.frontIndex,
        selectedBackVariationIndex: state.generation.images.selected.backIndex,
        ticketId: state.ticket.id,
        imageUrl: state.generation.images.front.url,
        imagePath: state.generation.images.front.path,
        frontDesignSpec: state.generation.images.front.designSpec,
        backImageUrl: state.generation.images.back.url,
        backImagePath: state.generation.images.back.path,
        backDesignSpec: state.generation.images.back.designSpec,
        timestamp: Date.now(),
      };

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
      } catch (error) {
        logger.warn("Failed to save variations to localStorage", {
          component: "usePersistence",
          error: error instanceof Error ? error.message : String(error),
        });
      }
    } else {
      // Clear localStorage if no variations
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Ignore errors when clearing
      }
    }
  }, [
    state.generation.images.variations.front,
    state.generation.images.variations.back,
    state.generation.images.selected.frontIndex,
    state.generation.images.selected.backIndex,
    state.ticket.id,
    state.generation.images.front.url,
    state.generation.images.front.path,
    state.generation.images.front.designSpec,
    state.generation.images.back.url,
    state.generation.images.back.path,
    state.generation.images.back.designSpec,
  ]);

  // Variations Persistence: Restore variations from localStorage on mount
  useEffect(() => {
    const frontVariations =
      state.generation.images.variations.front.length > 0
        ? state.generation.images.variations.front
        : null;
    const backVariations =
      state.generation.images.variations.back.length > 0
        ? state.generation.images.variations.back
        : null;

    // Only restore if no variations exist in current state
    if (frontVariations || backVariations) {
      return; // Already have variations, don't restore
    }

    try {
      // Ticket/gallery URL state and failed-adoption save points outrank the
      // legacy variation cache. Replaying that cache here can replace the
      // requested dress, or reattach the anonymous ticket id that
      // useInitialization deliberately cleared for a fresh owned submit.
      if (
        hasAuthoritativeCreateRestore(
          restorationTimestampRef.current !== null,
          true
        )
      ) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      const savedData = localStorage.getItem(STORAGE_KEY);
      if (!savedData) return;

      const parsed = JSON.parse(savedData);

      // Check expiration
      if (parsed.timestamp && Date.now() - parsed.timestamp > EXPIRATION_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Restore variations if they exist
      if (parsed.frontVariations || parsed.backVariations) {
        dispatch({
          type: "RESTORE_VARIATIONS",
          payload: {
            frontVariations: parsed.frontVariations || null,
            backVariations: parsed.backVariations || null,
            selectedFrontVariationIndex:
              parsed.selectedFrontVariationIndex ?? null,
            selectedBackVariationIndex:
              parsed.selectedBackVariationIndex ?? null,
            ticketId: parsed.ticketId || null,
            imageUrl: parsed.imageUrl || null,
            imagePath: parsed.imagePath || null,
            frontDesignSpec: parsed.frontDesignSpec,
            backImageUrl: parsed.backImageUrl || null,
            backImagePath: parsed.backImagePath || null,
            backDesignSpec: parsed.backDesignSpec,
          },
        });

        // Clear localStorage after successful restoration
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      logger.warn("Failed to restore variations from localStorage", {
        component: "usePersistence",
        error: error instanceof Error ? error.message : String(error),
      });
      // Clear corrupted data
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Ignore errors when clearing
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exit persistence: pagehide covers full-page exits; the cleanup covers
  // client-side navigation where the create tree unmounts without pagehide.
  useEffect(() => {
    let pageHideSavedLatestState = false;
    let skipUnmountFlushForBfcache = false;

    // A Zustand update can happen in the same browser task that unmounts this
    // hook, before React has time to rerender it with the new `state` prop.
    // Subscribe directly so the cleanup still sees that last manual edit.
    const unsubscribeFromStore = useCreatePageStore.subscribe(nextState => {
      latestStateRef.current = nextState;
      pageHideSavedLatestState = false;
    });

    const persistLatestStateOnExit = (): CreatePageState | null => {
      const latestState = latestStateRef.current;

      // Navbar sign-out clears account-owned create state immediately before
      // the full navigation. Without this guard, pagehide would serialize the
      // same ticket and images again, exposing them to the next visitor on a
      // shared browser.
      if (FormPersistenceService.isSignOutNavigationPending()) {
        logger.debug("Account sign-out in progress, skipping create save", {
          component: "usePersistence",
        });
        return null;
      }

      if (
        hasAuthoritativeCreateRestore(
          latestRestorationTimestampRef.current.current !== null
        )
      ) {
        logger.debug(
          "Authoritative create restore in progress, skipping save",
          {
            component: "usePersistence",
          }
        );
        return null;
      }

      // Save if there's any meaningful data
      if (!hasMeaningfulFormData(latestState)) {
        return null;
      }

      // Don't overwrite credit purchase save point
      if (latestState.ui.modals.credit.checkout) return null;

      // Check if there's already a recent credit purchase save point - don't overwrite it
      const existingSavePoint = SavePoint.getLatestSavePoint();
      if (
        existingSavePoint &&
        existingSavePoint.source === "credit_purchase_redirect" &&
        Date.now() - existingSavePoint.savedAt < 30000
      ) {
        // Within 30 seconds
        logger.debug("Not overwriting recent credit purchase save point", {
          component: "usePersistence",
        });
        return null;
      }

      // Save state for potential restoration
      FormPersistenceService.saveState({
        data: serializeFormState(latestState),
        source: "before_unload",
      });

      return latestState;
    };

    const handlePageHide = (event: PageTransitionEvent) => {
      // If page is being restored from bfcache, don't save (bfcache preserves state)
      if (event.persisted) {
        skipUnmountFlushForBfcache = true;
        logger.debug("Page restored from bfcache, skipping save", {
          component: "usePersistence",
        });
        return;
      }

      const persistedState = persistLatestStateOnExit();
      if (!persistedState) return;
      pageHideSavedLatestState = true;

      // Track detailed abandon event for funnel analysis
      const timeOnPage = Math.round(
        (Date.now() - pageLoadTimeRef.current) / 1000
      );
      const hasGeneratedImage = !!(
        persistedState.generation.images.front.url ||
        persistedState.generation.images.front.path
      );
      const hasPrompt = persistedState.form.data.prompt.trim().length >= 10;

      trackCreateAbandon({
        step: persistedState.form.currentStep,
        has_prompt: hasPrompt,
        has_generated_image: hasGeneratedImage,
        time_on_page_seconds: timeOnPage,
        reason: "page_unload",
      });

      // Also track legacy event for backwards compatibility
      trackDressDesignEvent(
        "abandon",
        persistedState.form.data.prompt || "upload",
        persistedState.form.currentStep
      );
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      // Page was restored from bfcache - state is already preserved
      if (event.persisted) {
        skipUnmountFlushForBfcache = false;
        logger.debug("Page restored from bfcache", {
          component: "usePersistence",
        });
      }
    };

    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
      unsubscribeFromStore();

      if (!pageHideSavedLatestState && !skipUnmountFlushForBfcache) {
        persistLatestStateOnExit();
      }
    };
  }, []);
}
